import { Briefcase } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface Props {
  isBusinessOwner?: boolean | null;
  role?: string | null;
  companyName?: string | null;
  variant?: 'full' | 'compact';
  className?: string;
}

/**
 * "Business Owner" tag shown on people cards when a user has ticked
 * is_business_owner AND their role is parent / nanny / childminder.
 * ('other' role already reads as "Business" via getRoleLabel — no double-badge.)
 */
export const BusinessOwnerBadge = ({
  isBusinessOwner,
  role,
  companyName,
  variant = 'full',
  className,
}: Props) => {
  if (!isBusinessOwner) return null;
  if (!role || role === 'other') return null;

  if (variant === 'compact') {
    return (
      <Badge
        variant="outline"
        className={cn(
          'gap-1 px-1.5 py-0 text-[10px] font-medium bg-primary/15 text-primary-foreground border-primary/40 backdrop-blur-sm',
          className,
        )}
      >
        <Briefcase className="h-2.5 w-2.5" />
        Business
      </Badge>
    );
  }

  const label = companyName
    ? `Business Owner · ${companyName}`
    : 'Business Owner';

  return (
    <Badge
      variant="outline"
      className={cn(
        'gap-1 px-1.5 py-0 text-[11px] font-medium bg-primary/10 text-primary border-primary/30 max-w-full',
        className,
      )}
    >
      <Briefcase className="h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
    </Badge>
  );
};
